/**
 * Link management — `screens.md` §5, **critical fidelity**, and the screen the phase's
 * headline exit criterion is written about.
 *
 * > *"Someone who has never heard of GS1 can look at the screen, seeded with the Riverbend
 * > example, and correctly answer **what does a scan of batch LOT42 resolve to, and
 * > why?**"* — `acceptance-tests.md`
 *
 * Everything here serves that sentence. The four-word vocabulary answers *why*; the
 * simulator answers *what*; and the two are wired to the **same** source of truth so they
 * cannot disagree — the merge endpoint's `holdsDefaultSlot` labels the rows, and the same
 * response's `destination` is what the simulator shows.
 *
 * ## The decisions are functions, and the components call them
 *
 * `link-row.ts` and `default-slot.ts` hold every rule this screen states. Nothing in this
 * file decides anything, and that is c34's finding applied before it could recur: logic
 * reachable only through an interaction is logic no test in this repo can reach, so it gets
 * tested by a copy of itself — a copy that stays green while the real screen changes.
 *
 * **That was not sufficient, and the gap is worth reading before adding a component here.**
 * Extracting the *decisions* left the *renderings* behind a click: `viewed` is state this
 * component owns and a `<select>` moves, so a static render can only ever produce
 * GTIN-grain rows — and two of the four words had no subject in the suite until
 * {@link LinkRow} and {@link CreateLinkPanel} were exported. See `LinkRow`'s header. The
 * sharpened rule: **a decision extracted as a function still needs its rendering reachable,
 * and "reachable" means without an event.**
 *
 * ## The simulator is not a preview of the merge, it is the merge
 *
 * `acceptance-tests.md`: *"backed by the real merge endpoint, not a client-side guess."*
 * Every control here changes a query parameter on `GET /v1/products/{id}/linkset`, which
 * runs `buildEntry`, `mergeLinkset` and `negotiate` — the same three functions the resolver
 * runs on a scan. The mechanical half of that promise is a dependency-cruiser rule:
 * `apps/app` may not import `@packagepage/gs1`, so there is no merge in this bundle to
 * guess with.
 *
 * ## The batch-level link gate (F7)
 *
 * Below Growth, the create form at a lot or serial grain is {@link BatchLinkGate}. The mock
 * says such links stop serving after the trial; Q-P4 ruled they keep resolving, and the
 * copy follows the ruling — Drift §U63.
 *
 * ## Refusals are rendered from the API's own problem body
 *
 * `RECALL_LINK_LOCKED`, `DEFAULT_IN_RECALL_SCOPE` and `RECALL_HEAD_ORDER` all carry authored
 * `detail` copy naming the recall id and the remedy. `screens.md` §5 is explicit that the
 * row renders `detail` **from the body** rather than composing its own message — disabled
 * controls are UI courtesy and the API refuses anyway, and a second wording of a sentence a
 * person acts on during a safety incident is a second source of truth for it.
 */
import { useMemo, useState, type ReactNode } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { Button, Pill, PlanGate, Surface } from "@packagepage/ui";
import {
  BATCH_LINK_TIER,
  digitalLinkPath,
  isBatchGrain,
  planRank,
  type LinkSummary,
  type LinksetPreview,
  type Tier,
} from "@packagepage/shared";

import { problemOf } from "../api/client.js";
import { api } from "../api/procedures.js";
import {
  defaultSlotOccupant,
  defaultSlotQuestion,
  gtinDefaultLock,
} from "./default-slot.js";
import {
  labelLinkRows,
  type LinkRowLabels,
  type RecallContext,
  type ViewedGrain,
} from "./link-row.js";

import "@packagepage/ui/button.css";
import "@packagepage/ui/gate.css";
import "@packagepage/ui/pill.css";
import "@packagepage/ui/surface.css";
import "./link-management.css";

const EMPTY_GRAIN: ViewedGrain = { cpv: null, lot: null, serial: null };

const grainOf = (link: LinkSummary): ViewedGrain => ({
  cpv: link.grain.cpv ?? null,
  lot: link.grain.lot ?? null,
  serial: link.grain.serial ?? null,
});

const nameOf = (link: LinkSummary): string => link.title ?? link.href;

/** Clear `occupant`'s flag, run `claim`, and put the flag back if `claim` is refused. */
// Two PATCHes because the API has no swap. At the GTIN grain of a published product the
// first is refused (entry format needs a default), and that refusal is shown — Drift Log U23.
export async function withDefaultMoved<T>(
  occupant: LinkSummary | null,
  claim: () => Promise<T>,
): Promise<T> {
  if (occupant === null) return claim();
  await api.updateLink(occupant.id, { isDefault: false });
  try {
    return await claim();
  } catch (error) {
    await api.updateLink(occupant.id, { isDefault: true }).catch(() => undefined);
    throw error;
  }
}

export function LinkManagement({ productId }: { productId: string }): ReactNode {
  const queryClient = useQueryClient();

  /**
   * The scan being asked about. **One piece of state, read by both panels** — the rows and
   * the simulator answer the same question and must never be able to answer it about
   * different scans.
   */
  const [viewed, setViewed] = useState<ViewedGrain>(EMPTY_GRAIN);
  const [lang, setLang] = useState<string>("");
  const [linkType, setLinkType] = useState<string>("");
  const [refusal, setRefusal] = useState<{ title: string; detail: string } | null>(null);
  const [pendingSwap, setPendingSwap] = useState<{
    target: LinkSummary;
    occupant: LinkSummary;
  } | null>(null);

  const org = useQuery({
    queryKey: ["orgs", "current"],
    queryFn: () => api.currentOrg(),
  });
  const links = useQuery({
    queryKey: ["links", productId],
    queryFn: () => api.links(productId),
  });
  const variants = useQuery({
    queryKey: ["variants", productId],
    queryFn: () => api.variants(productId),
  });
  const batches = useQuery({
    queryKey: ["batches", productId],
    queryFn: () => api.batches(productId),
  });
  const recalls = useQuery({ queryKey: ["recalls"], queryFn: () => api.recalls() });
  const linkTypes = useQuery({
    queryKey: ["link-types"],
    queryFn: () => api.linkTypes(),
  });

  /**
   * The preview, re-fetched whenever the scan changes.
   *
   * Keyed on every dimension so that changing the language re-asks the server rather than
   * re-deciding in the browser. That looks wasteful and is the whole point: the moment the
   * browser could answer "which link wins for `fr`" without asking, there would be a second
   * merge in the product, and c32's finding was an ordering between two correct functions.
   */
  const preview = useQuery({
    queryKey: ["linkset", productId, viewed, lang, linkType],
    queryFn: () =>
      api.linksetPreview(productId, {
        cpv: viewed.cpv,
        lot: viewed.lot,
        serial: viewed.serial,
        lang: lang === "" ? null : lang,
        linkType: linkType === "" ? null : linkType,
        accept: null,
      }),
  });

  /**
   * The active recalls on this product, each with what it suppressed.
   *
   * One detail fetch per active recall rather than a field on the list, because
   * `restore.suppressed` is unbounded in principle and belongs on the detail. R6 makes this
   * at most one request in practice — a GTIN carries at most one active recall — which is
   * why the loop is honest rather than a fan-out waiting to happen.
   *
   * **The `status === "active"` filter is load-bearing, not tidiness.** `restore.suppressed`
   * is what a recall *captured*, and it survives the lift by design — the column is the only
   * structured record of what the recall held, and `recall_events`' `restore_reinstated` is
   * what says whether each entry came back. So a lifted recall's detail still names links
   * that are no longer suppressed, and feeding one to `labelLinkRows` would mark a perfectly
   * ordinary row *"suspended by the recall of …"* forever. Measured in c35's parity slice,
   * which is why it is written here rather than assumed.
   */
  const activeRecallIds = useMemo(
    () =>
      (recalls.data?.recalls ?? [])
        .filter((r) => r.productId === productId && r.status === "active")
        .map((r) => r.id),
    [recalls.data, productId],
  );

  const recallDetails = useQuery({
    queryKey: ["recall-details", activeRecallIds],
    enabled: activeRecallIds.length > 0,
    queryFn: async () => Promise.all(activeRecallIds.map((id) => api.recall(id))),
  });

  const recallContexts: RecallContext[] = useMemo(
    () =>
      (recallDetails.data ?? []).map((detail) => ({
        id: detail.id,
        scope: detail.scope,
        lots: detail.lots,
        suppressed: detail.restore.suppressed,
      })),
    [recallDetails.data],
  );

  const rows: LinkRowLabels[] = useMemo(
    () =>
      labelLinkRows({
        links: links.data?.links ?? [],
        viewed,
        recalls: recallContexts,
        // The endpoint's answer wins wherever it is available — see `link-row.ts` on why
        // the local fallback is deliberately the weaker claim.
        holdsDefaultSlot:
          preview.data === undefined
            ? undefined
            : new Set(
                preview.data.links
                  .filter((l) => l.holdsDefaultSlot)
                  .map((l) => idOf(l.href, links.data?.links ?? [])),
              ),
        currentDestination: preview.data?.destination ?? null,
      }),
    [links.data, viewed, recallContexts, preview.data],
  );

  const onRefusal = (error: unknown) => {
    const problem = problemOf(error);
    setRefusal(
      problem === null
        ? {
            title: "That change did not go through",
            detail:
              "Nothing was changed. Printed codes are unaffected — the resolver answers " +
              "them from its own mirror and does not depend on this screen.",
          }
        : // **The API's own copy, verbatim.** `screens.md` §5 requires it: the body names
          // the recall id and the lift path, and this screen composing a second sentence
          // would be a second source of truth for it.
          { title: problem.title, detail: problem.detail },
    );
  };

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: ["links", productId] });
    void queryClient.invalidateQueries({ queryKey: ["linkset", productId] });
  };

  const deactivate = useMutation({
    mutationFn: (linkId: string) => api.deactivateLink(linkId),
    onSuccess: () => {
      setRefusal(null);
      invalidate();
    },
    onError: onRefusal,
  });

  const activate = useMutation({
    mutationFn: (linkId: string) => api.activateLink(linkId),
    onSuccess: () => {
      setRefusal(null);
      invalidate();
    },
    onError: onRefusal,
  });

  const remove = useMutation({
    mutationFn: (linkId: string) => api.deleteLink(linkId),
    onSuccess: () => {
      setRefusal(null);
      invalidate();
    },
    onError: onRefusal,
  });

  const makeDefault = useMutation({
    mutationFn: (swap: { target: LinkSummary; occupant: LinkSummary | null }) =>
      withDefaultMoved(swap.occupant, () =>
        api.updateLink(swap.target.id, { isDefault: true }),
      ),
    onSuccess: () => {
      setPendingSwap(null);
      setRefusal(null);
      invalidate();
    },
    onError: (error) => {
      setPendingSwap(null);
      onRefusal(error);
      invalidate();
    },
  });

  const allLinks = links.data?.links ?? [];

  const canHoldDefault = (link: LinkSummary): boolean =>
    !link.isDefault &&
    link.active &&
    ((linkTypes.data?.linkTypes ?? []).find((t) => t.curie === link.linkType)
      ?.isDefaultEligible ??
      true);

  const requestDefault = (target: LinkSummary) => {
    const occupant = defaultSlotOccupant(
      allLinks,
      { grain: grainOf(target), hreflang: target.hreflang, mediaType: target.mediaType },
      target.id,
    );
    if (occupant === null) makeDefault.mutate({ target, occupant: null });
    else setPendingSwap({ target, occupant });
  };

  if (links.isPending) {
    return (
      <Surface>
        <p data-screen-label="Link management">
          Loading this product&rsquo;s links&hellip;
        </p>
      </Surface>
    );
  }

  if (links.error !== null) {
    const problem = problemOf(links.error);
    return (
      <Surface tone="error" emphasis="filled">
        <div data-screen-label="Link management">
          <strong>{problem?.title ?? "We could not load these links"}</strong>
          <p>
            {problem?.detail ??
              "Nothing was changed. Printed codes are unaffected — the resolver answers " +
                "them from its own mirror and does not depend on this screen."}
          </p>
        </div>
      </Surface>
    );
  }

  const scanPath = digitalLinkPath(preview.data?.gtin14 ?? "", viewed);

  return (
    <div data-screen-label="Link management" className="pp-links">
      {/* ------------------------------------------------------------------ *
       * The simulator. It leads, because it states the answer the rows explain.
       * ------------------------------------------------------------------ */}
      <Surface>
        <div className="pp-links__sim">
          <h2 className="pp-links__heading">What does a scan of this code get?</h2>

          <div className="pp-links__controls">
            <label className="pp-links__control">
              <span>Variant</span>
              <select
                value={viewed.cpv ?? ""}
                onChange={(e) =>
                  setViewed((g) => ({
                    ...g,
                    cpv: e.target.value === "" ? null : e.target.value,
                  }))
                }
              >
                <option value="">No variant on the code</option>
                {(variants.data?.variants ?? []).map((v) => (
                  <option key={v.id} value={v.cpv}>
                    {v.cpv}
                  </option>
                ))}
              </select>
            </label>

            <label className="pp-links__control">
              <span>Batch</span>
              <select
                value={viewed.lot ?? ""}
                onChange={(e) =>
                  setViewed((g) => ({
                    ...g,
                    lot: e.target.value === "" ? null : e.target.value,
                  }))
                }
              >
                <option value="">No batch on the code</option>
                {(batches.data?.batches ?? []).map((b) => (
                  <option key={b.id} value={b.lot}>
                    {b.lot}
                  </option>
                ))}
              </select>
            </label>

            <label className="pp-links__control">
              <span>Phone language</span>
              <select value={lang} onChange={(e) => setLang(e.target.value)}>
                {/* The header matrix that measured c32's defect. The last one is what a
                    real phone sends, and it is the row an ordering mistake shows up in. */}
                <option value="">No preference</option>
                <option value="en">en</option>
                <option value="fr">fr</option>
                <option value="en-GB,en;q=0.9">en-GB,en;q=0.9</option>
              </select>
            </label>

            <label className="pp-links__control">
              <span>Asking for</span>
              <select value={linkType} onChange={(e) => setLinkType(e.target.value)}>
                {/* Empty is a phone camera, and it is the default because it is the case
                    the whole default-slot ruling is about. */}
                <option value="">A phone camera (no link type)</option>
                {[...new Set((links.data?.links ?? []).map((l) => l.linkType))].map(
                  (t) => (
                    <option key={t} value={t}>
                      {t}
                    </option>
                  ),
                )}
              </select>
            </label>
          </div>

          {/* `acceptance-tests.md`: *"Digital Link URIs are shown, monospaced and copyable,
              wherever a user might need proof the system is doing something real."* This is
              that place — the path being previewed is the literal thing a scanner requests,
              and a user checking our answer against a real scan needs to be able to take it
              away. Monospaced without copyable is half the item. */}
          <div className="pp-links__scan">
            <span className="pp-links__mono">{scanPath}</span>
            <Button
              variant="quiet"
              onClick={() => void navigator.clipboard?.writeText(scanPath)}
            >
              Copy
            </Button>
          </div>

          {preview.isPending ? (
            <p>Asking the server what this scan resolves to&hellip;</p>
          ) : preview.data === undefined ? (
            <p>The preview is unavailable. Nothing about your links has changed.</p>
          ) : (
            <SimulatorAnswer preview={preview.data} />
          )}
        </div>
      </Surface>

      {refusal === null ? null : (
        <Surface tone="error" emphasis="filled">
          <div className="pp-links__refusal">
            <strong>{refusal.title}</strong>
            <p>{refusal.detail}</p>
          </div>
        </Surface>
      )}

      {pendingSwap === null ? null : (
        <DefaultSwap
          target={pendingSwap.target}
          occupant={pendingSwap.occupant}
          scanPath={digitalLinkPath(
            pendingSwap.target.gtin14,
            grainOf(pendingSwap.target),
          )}
          lock={
            grainOf(pendingSwap.target).cpv === null &&
            grainOf(pendingSwap.target).lot === null &&
            grainOf(pendingSwap.target).serial === null
              ? gtinDefaultLock(
                  preview.data?.status ?? null,
                  digitalLinkPath(pendingSwap.target.gtin14),
                  pendingSwap.occupant.title,
                )
              : null
          }
          busy={makeDefault.isPending}
          onKeep={() => setPendingSwap(null)}
          onMove={() => makeDefault.mutate(pendingSwap)}
        />
      )}

      {/* ------------------------------------------------------------------ *
       * The rows. Each one says where it comes from and what it does.
       * ------------------------------------------------------------------ */}
      <Surface>
        <h2 className="pp-links__heading">Links that apply to this scan</h2>
        {rows.length === 0 ? (
          <p>
            No link applies to this scan yet. A plain camera scan needs one link holding
            the GTIN&rsquo;s default slot before this product can be published.
          </p>
        ) : (
          <ul className="pp-links__rows">
            {rows.map((row) => (
              <LinkRow
                key={row.link.id}
                row={row}
                busy={
                  deactivate.isPending ||
                  activate.isPending ||
                  remove.isPending ||
                  makeDefault.isPending
                }
                onDeactivate={() => deactivate.mutate(row.link.id)}
                onActivate={() => activate.mutate(row.link.id)}
                onDelete={() => remove.mutate(row.link.id)}
                onMakeDefault={
                  canHoldDefault(row.link) ? () => requestDefault(row.link) : undefined
                }
              />
            ))}
          </ul>
        )}
      </Surface>

      {/* ------------------------------------------------------------------ *
       * Adding a link — and the question the Default Slot ruling requires.
       * ------------------------------------------------------------------ */}
      <Surface>
        <CreateLinkPanel
          productId={productId}
          viewed={viewed}
          scanPath={scanPath}
          incumbent={preview.data?.selected ?? null}
          slotOccupant={defaultSlotOccupant(
            allLinks,
            { grain: viewed, hreflang: null, mediaType: null },
            null,
          )}
          productStatus={preview.data?.status ?? null}
          plan={org.data?.plan ?? null}
          onRefusal={(error) => {
            onRefusal(error);
            invalidate();
          }}
          onCreated={() => {
            setRefusal(null);
            invalidate();
          }}
        />
      </Surface>
    </div>
  );
}

/**
 * The create-link form, and the one question `Decisions/2026-08-04 The Default Slot`
 * makes a deliverable.
 *
 * > *Creating a link at a finer grain always asks, in scan terms, where plain scans of that
 * > grain should land.* … **Silence is wrong under every reading.**
 *
 * Every rule the question embodies — whether to ask, which answer is pre-selected, why, and
 * the consequence of each — is `defaultSlotQuestion` in `default-slot.ts`. This component
 * renders what that function returns and decides nothing, which is the arrangement c34's
 * retire-dialog finding argues for: a rule inside a form is a rule behind a click, and a
 * rule behind a click gets tested by a hand-copy of the JSX that stays green while the real
 * form changes.
 *
 * **The incumbent comes from the merge preview, not from the rows.** Which type currently
 * holds the slot a new link would shadow is a merge question, and asking the rows would be
 * the client-side guess the whole endpoint exists to avoid — one sentence smaller and just
 * as wrong.
 *
 * `query pass-through is stated where a link is created` (§4, c17): a printed
 * `?utm_source=` is a promise, so the form says what happens to it rather than leaving a
 * brand to discover it from a redirect.
 */
export function CreateLinkPanel({
  productId,
  viewed,
  scanPath,
  incumbent,
  slotOccupant,
  productStatus,
  plan,
  onRefusal,
  onCreated,
}: {
  productId: string;
  viewed: ViewedGrain;
  scanPath: string;
  incumbent: LinksetPreview["selected"];
  /** The row holding the exact slot this form writes — grain, no language, no media type. */
  slotOccupant: LinkSummary | null;
  /** From the merge preview; null while it loads. */
  productStatus: string | null;
  /** `orgs.current`'s plan; null while it loads, and the API refuses regardless. */
  plan: Tier | null;
  onRefusal: (error: unknown) => void;
  onCreated: () => void;
}): ReactNode {
  if (
    isBatchGrain(viewed) &&
    plan !== null &&
    planRank(plan) < planRank(BATCH_LINK_TIER)
  ) {
    return <BatchLinkGate scanPath={scanPath} />;
  }
  return (
    <CreateLinkForm
      productId={productId}
      viewed={viewed}
      scanPath={scanPath}
      incumbent={incumbent}
      slotOccupant={slotOccupant}
      productStatus={productStatus}
      onRefusal={onRefusal}
      onCreated={onCreated}
    />
  );
}

/** `screens.md` §5's trial gate on batch-level links. It states the fallback, not the loss. */
export function BatchLinkGate({ scanPath }: { scanPath: string }): ReactNode {
  return (
    <div data-screen-label="Batch-level link gate">
      <PlanGate
        plan={BATCH_LINK_TIER}
        feature="Links for one batch or one serial number come with Growth"
        why={
          "A batch-level link gives one lot or one unit its own destination, such as a " +
          "batch advisory or a certificate, and it is the point where a catalogue is " +
          "managed lot by lot rather than product by product."
        }
        instead={
          `Scans of ${scanPath} land on the product-wide links, which keep working on ` +
          "every plan. Batch-level links you already have keep resolving, and a recall " +
          "creates the ones it needs on any plan."
        }
      />
    </div>
  );
}

function CreateLinkForm({
  productId,
  viewed,
  scanPath,
  incumbent,
  slotOccupant,
  productStatus,
  onRefusal,
  onCreated,
}: {
  productId: string;
  viewed: ViewedGrain;
  scanPath: string;
  incumbent: LinksetPreview["selected"];
  slotOccupant: LinkSummary | null;
  productStatus: string | null;
  onRefusal: (error: unknown) => void;
  onCreated: () => void;
}): ReactNode {
  const [linkType, setLinkType] = useState("gs1:pip");
  const [href, setHref] = useState("");
  const [title, setTitle] = useState("");
  const [makeDefault, setMakeDefault] = useState<boolean | null>(null);
  const [warnings, setWarnings] = useState<readonly { code: string; detail: string }[]>(
    [],
  );

  const linkTypes = useQuery({
    queryKey: ["link-types"],
    queryFn: () => api.linkTypes(),
  });

  const isGtinGrain =
    viewed.cpv === null && viewed.lot === null && viewed.serial === null;

  const chosenType = (linkTypes.data?.linkTypes ?? []).find((t) => t.curie === linkType);

  const question = defaultSlotQuestion({
    scanPath,
    linkType,
    // Read from the registry rather than from a list of the three types GS1 restricts —
    // the snapshot is refreshed by release and a screen with its own copy would be a
    // fourth spelling of a fact the registry owns.
    isDefaultEligible: chosenType?.isDefaultEligible ?? true,
    incumbentLinkType: isGtinGrain
      ? (slotOccupant?.linkType ?? null)
      : (incumbent?.linkType ?? null),
    incumbentTitle: isGtinGrain
      ? (slotOccupant?.title ?? null)
      : (incumbent?.title ?? null),
    isGtinGrain,
    gtinLock: isGtinGrain
      ? gtinDefaultLock(productStatus, scanPath, slotOccupant?.title ?? null)
      : null,
  });

  // `null` means "the user has not touched it", so the ruling's pre-selection stands.
  // Tracked apart from the value so that changing the link type re-applies the
  // pre-selection for the *new* type, which is what makes it a recommendation rather than
  // a default the form forgets it made.
  const wantsDefault = makeDefault ?? question.preselected;

  const create = useMutation({
    mutationFn: () =>
      withDefaultMoved(question.ask && wantsDefault ? slotOccupant : null, () =>
        api.createLink(productId, {
          linkType,
          href,
          title: title === "" ? null : title,
          grain: viewed,
          isDefault: question.ask ? wantsDefault : false,
          active: true,
          hreflang: null,
          mediaType: null,
          validFrom: null,
          validTo: null,
        }),
      ),
    onSuccess: (result) => {
      setHref("");
      setTitle("");
      setMakeDefault(null);
      // `DEFAULT_SLOT_UNCLAIMED` rides on a 201 and is the machine-client half of the same
      // guard this form's question is the human half of. Surfaced rather than swallowed:
      // an advisory the API bothered to author and the screen drops is a warning nobody
      // gets.
      setWarnings(result.warnings);
      onCreated();
    },
    onError: (error) => {
      setWarnings([]);
      onRefusal(error);
    },
  });

  return (
    <form
      className="pp-links__create"
      onSubmit={(e) => {
        e.preventDefault();
        create.mutate();
      }}
    >
      <h2 className="pp-links__heading">Add a link at this grain</h2>
      <p className="pp-links__create-scan">
        It will answer scans of <span className="pp-links__mono">{scanPath}</span>.
      </p>

      <div className="pp-links__controls">
        <label className="pp-links__control">
          <span>Relation</span>
          <select value={linkType} onChange={(e) => setLinkType(e.target.value)}>
            {(linkTypes.data?.linkTypes ?? []).map((t) => (
              <option key={t.curie} value={t.curie}>
                {t.curie} — {t.title}
              </option>
            ))}
          </select>
        </label>

        <label className="pp-links__control pp-links__control--wide">
          <span>Destination</span>
          <input
            type="url"
            value={href}
            required
            placeholder="https://"
            onChange={(e) => setHref(e.target.value)}
          />
        </label>

        <label className="pp-links__control pp-links__control--wide">
          <span>Title</span>
          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Shown in the linkset, and required for a default"
          />
        </label>
      </div>

      {/* The ruling's question. Rendered whenever it applies, with **both** consequences
          visible — a form that described only the state it was in would make a user toggle
          a safety-relevant flag to find out what it does. */}
      {question.ask ? (
        <div className="pp-links__slot-question">
          <label className="pp-links__slot-choice">
            <input
              type="checkbox"
              checked={wantsDefault}
              onChange={(e) => setMakeDefault(e.target.checked)}
            />
            <span>Make this the destination for plain scans of this grain</span>
          </label>
          <p className="pp-links__slot-reason">{question.reason}</p>
          <p className="pp-links__slot-consequence">
            <strong>
              {wantsDefault ? question.consequenceIfDefault : question.consequenceIfNot}
            </strong>{" "}
            <span className="pp-links__slot-alternative">
              {wantsDefault ? question.consequenceIfNot : question.consequenceIfDefault}{" "}
              if you {wantsDefault ? "clear" : "tick"} the box.
            </span>
          </p>
        </div>
      ) : (
        <p className="pp-links__slot-reason">{question.reason}</p>
      )}

      {/* c17's `qsPassedOn`: the resolver carries a request's query string onto the
          redirect target. A brand printing `?utm_source=` on a carton is relying on that,
          so it is stated where the destination is typed rather than discovered from a
          redirect months later. `linkType` is dropped, because it is our control parameter
          rather than the client's data. */}
      <p className="pp-links__passthrough">
        Anything a scanned code carries in its query string is passed through to this
        destination — <span className="pp-links__mono">?utm_source=carton</span> arrives
        here. Our own <span className="pp-links__mono">?linkType=</span> is not forwarded.
      </p>

      <CreateWarnings warnings={warnings} />

      <Button type="submit" disabled={create.isPending || href === ""}>
        {create.isPending ? "Adding…" : "Add link"}
      </Button>
    </form>
  );
}

/**
 * What the endpoint said, rendered as the sentence the acceptance test asks for.
 *
 * Nothing here is computed. Every field comes off the response, which is what makes the
 * screen and a real scan the same answer rather than two answers that usually agree.
 */
function SimulatorAnswer({ preview }: { preview: LinksetPreview }): ReactNode {
  return (
    <div className="pp-links__answer">
      {preview.blocked === null ? null : (
        <Surface tone="warn" emphasis="filled">
          <div>
            <strong>
              {preview.blocked === "not_published"
                ? "This code is not answering yet"
                : "This code cannot answer any scan"}
            </strong>
            <p>{preview.blockedDetail}</p>
          </div>
        </Surface>
      )}

      {preview.destination === null ? (
        <p className="pp-links__destination">
          {preview.outcome === "no_such_link_type"
            ? "Nothing — no link on this code declares that relation, so a client asking " +
              "for it gets an honest 404 rather than a page it did not ask for."
            : "Nothing resolves for this scan."}
        </p>
      ) : (
        <p className="pp-links__destination">
          <span className="pp-links__arrow">&rarr;</span>{" "}
          <span className="pp-links__mono">{preview.destination}</span>
          {preview.selected?.title === null || preview.selected === null ? null : (
            <span className="pp-links__dest-title"> — {preview.selected.title}</span>
          )}
        </p>
      )}

      {preview.recallId === null ? null : (
        <p className="pp-links__recall-note">
          This scan falls inside an active recall&rsquo;s scope, so it is counted as a
          scan of an affected unit — whether or not the notice is where it landed.
        </p>
      )}
    </div>
  );
}

/**
 * The advisories a successful create came back with — **its own component because the
 * branch was otherwise unreachable, which is the third instance of one shape in this
 * commit.**
 *
 * `DEFAULT_SLOT_UNCLAIMED` rides on a **201**: the API accepted the link and is telling the
 * caller that a plain scan of that grain will not reach it. It is the machine-client half
 * of the same guard the default-slot question is the human half of, and a screen that
 * dropped it would leave an integrator's warning with no reader.
 *
 * It lived inline in {@link CreateLinkPanel} and was set only from a mutation's
 * `onSuccess`, so a static render always saw the initial empty array and **no test in this
 * repository could reach the rendering.** Same shape as `LinkRow`'s, one layer smaller, and
 * found the same way — by an agent reporting that it could not get there rather than
 * writing something that looked like it had.
 *
 * The general rule this commit ends up stating three times: **state set only by an event is
 * state a static render cannot produce, so anything it gates needs a component of its own.**
 */
export function CreateWarnings({
  warnings,
}: {
  warnings: readonly { code: string; detail: string }[];
}): ReactNode {
  if (warnings.length === 0) return null;
  return (
    <Surface tone="warn" emphasis="filled">
      <div className="pp-links__warnings">
        {warnings.map((w) => (
          <p key={w.code}>{w.detail}</p>
        ))}
      </div>
    </Surface>
  );
}

/**
 * One row — **exported, and the export is the deliverable rather than a convenience.**
 *
 * c35's test slice reported that `inherited` and `replaced` were **structurally
 * unreachable** through a render of `LinkManagement`: `viewed` is `useState(EMPTY_GRAIN)`
 * owned inside the component and moved only by a `<select>`'s `onChange`, and
 * `renderToStaticMarkup` fires no events — so `appliesTo` lets only GTIN-grain links
 * through, and every row the suite could ever render was `defined here`. Two of the four
 * words `screens.md` §5 calls *"vocabulary that must survive implementation"* had no
 * subject.
 *
 * This is c34's retire-dialog finding arriving one commit later in a new place, and the
 * agent got the important half right: it **reported the gap instead of reconstructing the
 * JSX**, which is what would have produced a green test for a screen that had stopped
 * rendering the words. The rule, stated again because the project keeps re-learning it:
 * *logic reachable only through an interaction is logic no test in this repo can reach, so
 * it gets tested by a copy of itself — and the copy stays green.*
 *
 * The fix is the same one `RetireDialog` took: export the component so its real markup is
 * what a test renders. Lifting `viewed` to a prop was the alternative and is worse — a prop
 * that exists only for tests is a second way to configure the screen, and the two can
 * disagree.
 */
export function LinkRow({
  row,
  busy,
  onDeactivate,
  onActivate,
  onDelete,
  onMakeDefault,
}: {
  row: LinkRowLabels;
  busy: boolean;
  onDeactivate: () => void;
  onActivate: () => void;
  onDelete: () => void;
  /** Absent when the row cannot claim its slot: already default, switched off, or restricted. */
  onMakeDefault?: (() => void) | undefined;
}): ReactNode {
  const { link, recall } = row;
  const materialised = recall.kind === "materialised";

  return (
    <li className="pp-links__row" data-recall-state={recall.kind}>
      <div className="pp-links__row-main">
        <span className="pp-links__type">{link.linkType}</span>
        <span className="pp-links__mono pp-links__href">{link.href}</span>
        {link.title === null ? null : (
          <span className="pp-links__title">{link.title}</span>
        )}
      </div>

      {/*
        The four words are **not pills**, and that is a decision rather than an oversight.

        `components.md` gives `Pill` exactly four families — product · domain · prefix ·
        plan gate — each a frozen value→tone table, and none of them is this vocabulary.
        Adding a fifth family to make `defined here` fit would be widening a frozen
        vocabulary to suit one screen, which is the Phase 0 scaffold's mistake (c22: three
        of six tones and all six of the other values disagreed with the spec, and it sat
        green for a whole phase because nothing imported it).

        They are also not statuses. A pill in this product says *what state a thing is in*;
        these say *where a row came from and what it does for this scan* — two axes, which
        is why `default` sits beside `inherited` rather than replacing it.
      */}
      <div className="pp-links__row-labels">
        <span className="pp-links__label" data-label={row.origin}>
          {row.origin}
        </span>
        {row.replaced ? (
          <span
            className="pp-links__label"
            data-label="replaced"
            title="A finer link of this same type answers this scan instead. Shadowing is per link type — links of other types are untouched."
          >
            replaced
          </span>
        ) : null}
        {row.holdsDefaultSlot ? (
          <span
            className="pp-links__label"
            data-label="default"
            title="A plain camera scan of this grain lands here."
          >
            default
          </span>
        ) : null}
        {link.active ? null : (
          <Pill family="product" value="archived">
            switched off
          </Pill>
        )}
      </div>

      {recall.kind === "materialised" ? (
        <p className="pp-links__row-note pp-links__row-note--recall">
          Created by the recall flow. It is what sends a scan of an affected unit to the
          notice, so it cannot be changed or removed while that recall is active — lift
          the recall when the hazard has passed.
        </p>
      ) : null}

      {recall.kind === "suppressed" ? (
        <p className="pp-links__row-note">
          <strong>
            Suspended by the recall of {recall.scopeDescription} — restored automatically
            when it is lifted.
          </strong>{" "}
          {row.meanwhileDestination === null ? null : (
            <>
              Meanwhile, plain scans of this code land on{" "}
              <span className="pp-links__mono">{row.meanwhileDestination}</span>.{" "}
            </>
          )}
          You can still edit it. <strong>Deleting it now is permanent</strong>: a
          suspended link that is gone at lift is skipped, and it does not come back.
        </p>
      ) : null}

      <div className="pp-links__row-actions">
        {onMakeDefault === undefined ? null : (
          <Button variant="quiet" disabled={materialised || busy} onClick={onMakeDefault}>
            Make default
          </Button>
        )}
        {link.active ? (
          <Button variant="quiet" disabled={materialised || busy} onClick={onDeactivate}>
            Switch off
          </Button>
        ) : (
          <Button variant="quiet" disabled={materialised || busy} onClick={onActivate}>
            Switch on
          </Button>
        )}
        <Button variant="quiet" disabled={materialised || busy} onClick={onDelete}>
          Delete
        </Button>
      </div>
    </li>
  );
}

/**
 * The design's state B — a second default at the same grain is a swap, explained, with the
 * safe choice on the left. Exported so a static render can reach it without the click.
 */
export function DefaultSwap({
  target,
  occupant,
  scanPath,
  lock,
  busy,
  onKeep,
  onMove,
}: {
  target: LinkSummary;
  occupant: LinkSummary;
  scanPath: string;
  /** Non-null when the move cannot succeed; rendered in place of the Move button. */
  lock: string | null;
  busy: boolean;
  onKeep: () => void;
  onMove: () => void;
}): ReactNode {
  return (
    <Surface tone="warn" emphasis="filled">
      <div className="pp-links__swap" data-screen-label="Default swap">
        <strong>
          Make {nameOf(target)} the default for{" "}
          <span className="pp-links__mono">{scanPath}</span>?
        </strong>
        <p>
          A plain camera scan can only go to one place, so each grain has exactly one
          default. Its default is currently {nameOf(occupant)}.
        </p>
        {lock === null ? (
          <p>
            Making {nameOf(target)} the default moves {nameOf(occupant)} out of the
            default slot. It stays available to anything that asks for {occupant.linkType}{" "}
            by name.
          </p>
        ) : (
          <p className="pp-links__swap-lock">{lock}</p>
        )}
        <div className="pp-links__row-actions">
          <Button variant="secondary" disabled={busy} onClick={onKeep}>
            Keep current default
          </Button>
          {lock === null ? (
            <Button disabled={busy} onClick={onMove}>
              Move the default
            </Button>
          ) : null}
        </div>
      </div>
    </Surface>
  );
}

/**
 * Match a preview candidate back to the row it came from.
 *
 * The preview's links are entry-format records and carry no row id — the entry is what a
 * *scanner* gets and a row id would be a dashboard fact leaking onto a printed code's
 * payload. `href` is the join, which is sound because a link's href is what distinguishes
 * it to a consumer; two rows with one href are the same destination twice and either
 * answer labels the pair identically.
 */
function idOf(href: string, links: readonly LinkSummary[]): string {
  return links.find((l) => l.href === href)?.id ?? href;
}
